import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Star, ChevronRight, Building } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import SuggestClubModal from '../clubs/SuggestClubModal';

interface Venue {
  id: string;
  name: string;
  address: string;
  city: string;
  rating: number;
  image: string;
  isFallback?: boolean;
}

const FeaturedVenues: React.FC = () => {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userCity, setUserCity] = useState<string | null>(null);
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchUserCity = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        
        const { data } = await supabase
          .from('users')
          .select('city')
          .eq('auth_id', user.id)
          .single();
          
        if (data?.city) {
          setUserCity(data.city);
          // Don't fetch clubs here, let the other useEffect handle it
        }
      } catch (error) {
        console.error('Error fetching user city:', error);
      }
    };
    
    fetchUserCity();
  }, []);
  
  // Listen for city-changed event
  useEffect(() => {
    const handleCityChanged = (e: CustomEvent) => {
      const { city } = e.detail;
      setUserCity(city);
    };
    
    window.addEventListener('city-changed', handleCityChanged as EventListener);
    
    return () => {
      window.removeEventListener('city-changed', handleCityChanged as EventListener);
    };
  }, []);
  
  // Separate useEffect to fetch clubs when userCity changes
  useEffect(() => {
    if (userCity) {
      fetchClubs(userCity);
    }
  }, [userCity]);
  
  const fetchClubs = async (cityName: string) => {
    setIsLoading(true);
    console.log('Starting to fetch clubs for city:', cityName);
    try {
      // Extract the first part of the city name (before any comma)
 const CITY_ALIASES: Record<string, string> = {
  "new york": "new_york_city",
  "nyc": "new_york_city",
  "tel aviv": "tel_aviv",
  "tel aviv-yafo": "tel_aviv",
  "los angeles": "los_angeles",
  "la": "los_angeles",
  "san fran": "san_francisco",
  "sf": "san_francisco"
  // Add more if needed
};

// Normalize user city to lowercase snake_case
const normalizeToSnakeCase = (str: string) =>
  str.trim().toLowerCase().replace(/\s+/g, '_').replace(/-+/g, '_');

const inputCityKey = normalizeToSnakeCase(cityName.split(',')[0]);
const canonicalCity = CITY_ALIASES[inputCityKey] || inputCityKey;

console.log('🔍 Normalized city for DB lookup:', canonicalCity);

let { data: exactClubs, error: exactError } = await supabase
  .from('clubs')
  .select('club_id, club_name, formatted_address, city, rating, photo_urls, logo')
  .ilike('city', `%${canonicalCity}%`)
  .order('rating', { ascending: false })
  .limit(5);




      console.log('City search results:', exactClubs);
      
     if (!exactClubs || exactClubs.length === 0) {
  console.log('No clubs found, returning suggest-a-club fallback');
  setVenues([
    {
      id: 'suggest',
      name: 'Suggest a New Club',
      address: 'We couldn’t find any clubs in this city. Help us add one!',
      city: cityName,
      rating: 0,
      image: 'https://images.unsplash.com/photo-1584467735871-b7f3b4f7b2ec?auto=format&fit=crop&w=800&q=80',
      isFallback: true
    }
  ]);
  setIsLoading(false);
  return;
}

      
      // Process clubs data even if we only have one result
      if (exactClubs && exactClubs.length > 0) {
        console.log('Processing clubs data:', exactClubs);
        
        // Only transform real clubs (filter out test data first)
        const realClubs = exactClubs.filter(club => 
          !club.club_name.toLowerCase().includes('test') && 
          !club.club_name.toLowerCase().includes('my new')
        );
        
        if (realClubs.length === 0) {
          console.log('No real clubs found, using suggest club fallback');
          setVenues([
            {
              id: 'fallback',
              name: 'Suggest a Club in ' + cityName,
              address: 'No clubs found. Click to suggest a new one.',
              city: cityName,
              rating: 0,
              image: 'https://images.unsplash.com/photo-1622163642998-1ea32b0bbc67?auto=format&fit=crop&w=800&q=80',
              isFallback: true
            }
          ]);
          return;
        }
        
        const transformedVenues = realClubs.map(club => {
          // Debug log each club's photo_urls
          console.log(`Club ${club.club_name} photo_urls:`, club.photo_urls);
          
          let imageUrl = 'https://images.unsplash.com/photo-1622163642998-1ea32b0bbc67?auto=format&fit=crop&w=800&q=80';
          
          // Check if photo_urls is an array and has items
          if (Array.isArray(club.photo_urls) && club.photo_urls.length > 0) {
            imageUrl = club.photo_urls[0];
          } else if (club.logo) {
            imageUrl = club.logo;
          }
          
          return {
            id: club.club_id,
            name: club.club_name,
            address: club.formatted_address || 'Address not available',
            city: club.city || cityName,
            rating: club.rating || 4.5,
            image: imageUrl,
            isFallback: false
          };
        });
        
        console.log('Transformed venues:', transformedVenues);
        
        setVenues(transformedVenues);
      } else {
        console.log('No clubs found at all, using fallback');
        setVenues([
          {
            id: 'fallback',
            name: 'Error Loading Clubs in ' + cityName,
            address: 'Please try again later or suggest a new club',
            city: cityName,
            rating: 0,
            image: 'https://images.unsplash.com/photo-1622163642998-1ea32b0bbc67?auto=format&fit=crop&w=800&q=80',
            isFallback: true
          }
        ]);
      }
    } catch (error) {
      console.error('Error fetching clubs:', error);
      setVenues([
        {
          id: 'error',
          name: 'Error Loading Clubs',
          address: 'Please try again later',
          city: cityName,
          rating: 0,
          image: 'https://images.unsplash.com/photo-1622163642998-1ea32b0bbc67?auto=format&fit=crop&w=800&q=80',
          isFallback: true
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleVenueClick = (venueId: string) => {
    // Don't navigate for fallback venues
    if (venueId === 'error') {
      return;
    }
    
    // For fallback venue, open the suggest club modal
    if (venueId === 'fallback' || venueId === 'suggest') {
      setShowSuggestModal(true);
      return;
    }
    
    navigate(`/dashboard/clubs/${venueId}`);
  };
  
  const handleViewAllClick = () => {
    navigate('/dashboard/facilities', { 
      state: { selectedCity: userCity } 
    });
  };
  
  if (isLoading) {
    return (
      <div className="bg-dark-200 rounded-xl border border-dark-300 p-4 mb-6 animate-pulse">
        <div className="flex items-center justify-between mb-4">
          <div className="h-6 w-40 bg-dark-300 rounded"></div>
          <div className="h-6 w-24 bg-dark-300 rounded"></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-dark-300 rounded-lg h-48"></div>
          ))}
        </div>
      </div>
    );
  }
  
  if (venues.length === 0) return null;
  
  // Only show up to 3 venues
  const displayVenues = venues.slice(0, 3);
  
  return (
    <div className="bg-dark-200 rounded-xl border border-dark-300 p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Building className="h-5 w-5 text-gold-400 mr-2" />
          <h3 className="text-lg font-semibold text-white">Featured clubs in {userCity}</h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleViewAllClick}
          className="text-dark-800 hover:text-white"
        >
          View All
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {displayVenues.map((venue, index) => (
          <div
            key={`${venue.id}-${index}`}
            className={cn(
              "bg-dark-300 rounded-lg overflow-hidden group cursor-pointer hover:shadow-md",
             venue.isFallback && "border border-dashed border-gold-400/50 sm:col-span-3 md:col-span-2"
            )}
            onClick={() => handleVenueClick(venue.id)}
          >
            <div className="relative h-32">
              <img
                src={venue.image}
                alt={venue.id === 'fallback' ? 'Suggest a club' : venue.name}
                className={cn(
                  "w-full h-full object-cover",
                  venue.id !== 'fallback' && "transition-transform duration-300 group-hover:scale-110",
                 venue.id === 'fallback' && "opacity-40"
                )}
                onError={(e) => {
                  e.currentTarget.src = 'https://images.unsplash.com/photo-1622163642998-1ea32b0bbc67?auto=format&fit=crop&w=800&q=80';
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-dark-900/80 to-transparent"></div>
              {!venue.isFallback && venue.rating > 0 && (
                <div className="absolute bottom-2 left-2 flex items-center bg-dark-900/60 rounded-full px-2 py-0.5">
                  <Star className="h-3 w-3 text-gold-400 mr-1" />
                  <span className="text-xs text-white">{venue.rating.toFixed(1)}</span>
                </div>
              )}
              {venue.id === 'fallback' && (
               <div className="absolute inset-0 flex items-center justify-center">
                 <div className="bg-dark-900/80 px-6 py-3 rounded-lg text-center">
                   <Plus className="h-8 w-8 text-gold-400 mx-auto mb-2" />
                   <span className="text-base text-white font-semibold">Suggest a Club</span>
                  </div>
                </div>
              )}
            </div>
           <div className={cn("p-3", venue.isFallback && "p-4")}>
             <h4 className={cn("font-medium text-white mb-1 line-clamp-1", venue.isFallback ? "text-base text-center text-gold-400" : "text-sm")}>{venue.name}</h4>
             <div className={cn("flex items-start text-dark-800", venue.isFallback ? "text-sm justify-center" : "text-xs")}>
                <MapPin className="h-3 w-3 mr-1 mt-0.5 flex-shrink-0" />
                <span className="line-clamp-2">{venue.address}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Suggest Club Modal */}
      <SuggestClubModal 
        isOpen={showSuggestModal}
        onClose={() => setShowSuggestModal(false)}
        city={userCity || ''}
      />
    </div>
  );
};

export default FeaturedVenues;